import _ from 'underscore';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { MessageBar, MessageBarActions, MessageBarBody } from '@fluentui/react-message-bar';
import { Button, makeStyles, shorthands, } from '@fluentui/react-components';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import { DismissRegular } from '@fluentui/react-icons';
import { readFileAsDataURLAsync, UploadPreviewWithCustomPreview } from '@bingads-webui-campaign-react/upload-file-picker';
import { AdsStudioPanelContainer, supportedImageExtensions, supportedVideoExtensions, isRootFolderId, useUploadAsset, useCustomizedHandlerOptions, getFolderUploadError, createFolderPreviewComponent } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { useUploadFolder, NO_VALID_FILES_ERROR, ASSET_LIMIT_EXCEEDED_ERROR } from './hooks/use-upload-folder';

const useStyles = makeStyles({
  uploadButton: {
    width: '0px',
    ...shorthands.overflow('hidden !important'),
  },
});

const usePreviewStyle = makeStyles({
  imageItemBox: {
    marginBottom: '16px',
    marginRight: '16px',
    width: '298px',
    ...shorthands.borderRadius('4px'),
    boxShadow: '0px 0.3px 0.9px rgba(0, 0, 0, 0.1), 0px 1.6px 3.6px rgba(0, 0, 0, 0.13)',
    ...shorthands.overflow('hidden'),
    height: '130px',
  },
});

const useButtonStyles = makeStyles({
  bottonWrapper: {
    ...shorthands.padding('16px', '24px', '24px'),
  },
  button: {
    minWidth: 'auto',
    ...shorthands.padding('6px', '12px'),
    marginRight: '6px',
  },
  linkButton: {
    color: '#337ab7',
    fontWeight: 'normal',
    backgroundColor: 'transparent',
    ...shorthands.borderRadius('0px', '0px', '0px', '0px'),
    ...shorthands.border('none'),
  },
});

export const UploadFolderPanel = (props) => {
  const {
    i18n,
    dataService, aid, logger, notificationApi, perfMarker,
    navigation, mode, openSystemUpload, globalLegacyI18n,
    // disable image upscale feature for upload folder to avoid high QPS
    // isUploadImageUpscaleEnabled,
  } = props;
  const classes = useStyles();
  const previewClasses = usePreviewStyle();
  const buttonClasses = useButtonStyles();
  const [uploadFolderInfo, setUploadFolderInfo] = useState(null);
  const [showUploadPreview, setShowUploadPreview] = useState(false);
  const [errors, setErrors] = useState([]);
  const uploadInput = useRef();
  const completedAssetCount = useRef(0);
  const isUploadRunning = useRef(false);
  const assetCount = useRef(0);
  const fileAdditionalInfoArrRef = useRef([]);
  const { uploadFileAsync } = useUploadAsset({ dataService, perfMarker, isUploadImageUpscaleEnabled: false });
  const { getCustomizedHandlerOptions } = useCustomizedHandlerOptions();
  const globalI18n = useMemo(() => globalLegacyI18n.get('$/i18n!legacy/global'), [globalLegacyI18n]);

  const filePickerI18n = useMemo(() => mergeI18n(globalI18n, i18n), [globalI18n, i18n]);

  const {
    createFolderBulk,
    currentFolderId,
    cancelPendingVideoUpload,
    reloadFolderPathTreeItem,
  } = dataService;

  const dismiss = useCallback(({ reload }) => {
    fileAdditionalInfoArrRef.current = [];
    setErrors([]);
    setShowUploadPreview(false);
    setUploadFolderInfo(null);
    cancelPendingVideoUpload();
    navigation.goBack({ reload });
  }, [cancelPendingVideoUpload, navigation]);

  const dismissAndRefreshLibrary = useCallback(() => {
    isUploadRunning.current = false;
    dismiss({ reload: true });
  }, [dismiss]);

  const onRenderFooterContent = useCallback(() => (
    <div className={buttonClasses.bottonWrapper}>
      <Button className={buttonClasses.button} onClick={dismiss}>
        {i18n.getString(_TL_('Cancel'))}
      </Button>
    </div>
  ), [buttonClasses.bottonWrapper, buttonClasses.button, i18n, dismiss]);

  const onUploadError = useCallback((error) => {
    fileAdditionalInfoArrRef.current = [];
    isUploadRunning.current = false;
    setShowUploadPreview(false);
    if (error === NO_VALID_FILES_ERROR) {
      setErrors([i18n.getString(_TL_('Your folder doesn\'t contain supported assets. Please try again.'))]);
    } else if (error === ASSET_LIMIT_EXCEEDED_ERROR) {
      setErrors([i18n.getString(_TL_('Your folder failed to upload because it exceeds the 100-asset limit. Please try again'))]);
    } else {
      // no other known errors should cause the whole operation to fail, so just show generic error
      setErrors([i18n.getString(_TL_('We\'ve encountered an error and could not finish this task. Please try again or contact support.'))]);
    }
    logger.logError('Upload folder failed', { error });
  }, [i18n, logger]);

  const uploadAsset = useCallback(({
    dataURL,
    fileObject,
    nameWithoutExt,
    folderId,
    isVideo,
    isImageUpscaled,
  }) => uploadFileAsync({
    dataURL,
    fileObject,
    nameWithoutExt,
    folderId,
    isVideo,
  }).then((res) => {
    fileAdditionalInfoArrRef.current.push({
      isImageUpscaled,
      text: nameWithoutExt,
    });
    setUploadFolderInfo((prevState) => {
      if (prevState) {
        const rootFolder = prevState[0];
        completedAssetCount.current += 1;
        const updatedProgress = (completedAssetCount.current + 1) / assetCount.current;

        return [{
          ...rootFolder,
          progress: updatedProgress,
        }];
      }
      return prevState;
    });
    return res;
  }), [uploadFileAsync]);

  const onUploadStart = useCallback(({
    totalSize,
    rootFolderName,
    acceptedAssets,
  }) => {
    fileAdditionalInfoArrRef.current = [];
    setErrors([]);
    setShowUploadPreview(true);
    isUploadRunning.current = true;

    const previewImage = acceptedAssets[0];

    assetCount.current = acceptedAssets.length;

    readFileAsDataURLAsync(previewImage).then((imageData) => {
      setUploadFolderInfo([{
        name: rootFolderName,
        url: imageData,
        size: totalSize,
        progress: 0,
        numberOfAssets: assetCount.current,
      }]);
    });

    logger.logTrace(`Upload folder from local, assets number: ${assetCount.current}`);
  }, [logger]);

  const getErrorInfo = useCallback((folderCreationErrors, assetUploadErrors) => getFolderUploadError({
    folderCreationErrors,
    assetUploadErrors,
    i18n,
    filePickerI18n,
    aid,
    logger,
  }), [aid, i18n, logger, filePickerI18n]);

  const clearInputFile = useCallback((event) => {
    if (event) {
      // eslint-disable-next-line no-param-reassign
      event.target.value = '';
    }
  }, []);

  const onUploadPartialComplete = useCallback(({
    assetUploadErrors,
    folderCreationErrors,
  }) => {
    const warnMessage = getErrorInfo(folderCreationErrors, assetUploadErrors);
    logger.logTrace('Upload folder from local partial successfully');
    notificationApi.showWarning(warnMessage);
    dismissAndRefreshLibrary();
    reloadFolderPathTreeItem([currentFolderId]);
  }, [currentFolderId, dismissAndRefreshLibrary, getErrorInfo, logger, notificationApi, reloadFolderPathTreeItem]);

  const onUploadComplete = useCallback(() => {
    logger.logTrace('Upload folder from local successfully');

    const upscaledImagesArr = _.filter(fileAdditionalInfoArrRef.current, item => item && item.isImageUpscaled);
    const upscaledImagesArrCount = upscaledImagesArr.length;

    if (upscaledImagesArrCount > 0) {
      notificationApi.showInfo(i18n.getString(_TL_('Your folder was uploaded and we upscaled {{fileCount}} image(s) to meet the minimum required file size. You can always upload higher quality images if needed.'), { fileCount: upscaledImagesArrCount }));
    } else {
      notificationApi.showInfo((
        <span>
          {i18n.getString(_TL_('Your folder was successfully uploaded.'))}
          <button className={classNames(`HelpTopic ${buttonClasses.linkButton}`)} query="help:app54863">{i18n.getString(_TL_('Learn more about asset guidelines.'))}</button>
        </span>
      ));
    }
    dismissAndRefreshLibrary();
    reloadFolderPathTreeItem([currentFolderId]);
  }, [logger, notificationApi, i18n, buttonClasses.linkButton, dismissAndRefreshLibrary, reloadFolderPathTreeItem, currentFolderId]);

  const FolderPreviewComponent = useMemo(() => createFolderPreviewComponent({ i18n }), [i18n]);
  const isOpen = useMemo(() => Boolean((showUploadPreview && uploadFolderInfo) || errors.length > 0), [errors.length, showUploadPreview, uploadFolderInfo]);
  const supportedFileExtensions = useMemo(() => {
    if (mode === 'image') {
      return supportedImageExtensions;
    } else if (mode === 'video') {
      return supportedVideoExtensions;
    }
    return [
      ...supportedImageExtensions,
      ...supportedVideoExtensions,
    ];
  }, [mode]);

  const { onFolderRead } = useUploadFolder({
    createFolderAsync: createFolderBulk,
    currentFolderId: isRootFolderId(currentFolderId) ? undefined : currentFolderId,
    uploadAssetAsync: uploadAsset,
    getCustomizedHandlerOptions,
    onUploadError,
    onUploadStart,
    onUploadPartialComplete,
    onUploadComplete,
    supportedExtensions: supportedFileExtensions,
    clearInputFile,
    isUploadImageUpscaleEnabled: false,
    logger,
  });

  useEffect(() => {
    if (openSystemUpload) {
      uploadInput.current.click();
    }
  }, [openSystemUpload]);

  return (
    <>
      <AdsStudioPanelContainer
        dismiss={dismiss}
        isOpen={isOpen}
        headerText={i18n.getString(_TL_('Upload folder'))}
        onRenderFooterContent={onRenderFooterContent}
      >
        <>
          {showUploadPreview && uploadFolderInfo && (
            <UploadPreviewWithCustomPreview
              assetList={uploadFolderInfo}
              maxUploadNum={100}
              PreviewComponent={FolderPreviewComponent}
              customClasses={previewClasses}
              showTitle={false}
            />
          )}
          {
            errors.length > 0 &&
            errors.map((errMsg, index) => (
              <MessageBar
                className="error"
                // eslint-disable-next-line react/no-array-index-key
                key={`${index}`}
                intent="warning"
              >
                <MessageBarBody>
                  {errMsg}
                </MessageBarBody>
                <MessageBarActions
                  containerAction={
                    <Button
                      onClick={() => setErrors([])}
                      aria-label="dismiss"
                      appearance="transparent"
                      icon={<DismissRegular />}
                    />
                  }
                />
              </MessageBar>
            ))
          }
        </>
      </AdsStudioPanelContainer>
      <input
        ref={uploadInput}
        className={classNames('upload-folder-input', classes.uploadButton)}
        onChange={onFolderRead}
        type="file"
        multiple
        directory=""
        webkitdirectory=""
        tabIndex={-1}
        aria-label={i18n.getString(_TL_('upload folders'))}
        accept={supportedFileExtensions.join(',')}
      />
    </>
  );
};
UploadFolderPanel.propTypes = {
  aid: PropTypes.number.isRequired,
  i18n: PropTypes.shape({}).isRequired,
  navigation: PropTypes.shape({ gotoImagesPage: PropTypes.func }).isRequired,
  dataService: PropTypes.shape({
    uploadVideoByUrl: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  notificationApi: PropTypes.shape({
    showWarning: PropTypes.func,
  }).isRequired,
  mode: PropTypes.oneOf(['image', 'video']),
  openSystemUpload: PropTypes.shape({}),
};

UploadFolderPanel.defaultProps = {
  mode: null,
  openSystemUpload: null,
};
